import type { TranslationKey } from './en';

/**
 * Manjkajoč ključ je napaka ob prevajanju — to je glavna vrednost,
 * ki jo prodajajo i18n knjižnice, in tu je zastonj (T1 §11).
 *
 * Slog (P4): stavčna začetnica, velelnik brez ogovora za dejanja,
 * vikanje pri vprašanjih in pojasnilih. Nikoli tikanje, nikoli Title Case.
 */
export const sl: Record<TranslationKey, string> = {
  'app.name': 'Nesywave',

  'tab.stations': 'Postaje',
  'tab.search': 'Iskanje',
  'tab.history': 'Zgodovina',
  'tab.timer': 'Časovnik',
  'tab.settings': 'Nastavitve',

  'a11y.mainNav': 'Glavna navigacija',
  'a11y.brandHome': 'nesylab.com, odpre se v novem zavihku',
  'a11y.toggleTheme': 'Zamenjaj temo',
  'a11y.skipToContent': 'Skoči na vsebino',
  'a11y.stationList': 'Postaje',

  'player.play': 'Predvajaj',
  'player.stop': 'Ustavi',
  'player.previous': 'Prejšnja postaja',
  'player.next': 'Naslednja postaja',
  'player.nothing': 'Nič se ne predvaja',
  'player.pickStation': 'Izberite postajo',
  'player.connecting': 'Povezovanje…',
  'player.live': 'V živo',
  'player.retrying': 'Povezava prekinjena. Ponovni poskus {n} …',
  'player.offline': 'Postaja ne odgovarja',
  'player.blocked': 'Za začetek pritisnite gumb za predvajanje',
  'player.track': '{artist} — {title}',
  'player.announce': 'Zdaj igra: {track}',

  // Gumb pove, kaj bo naredil — dve imeni in ne `aria-pressed` (P2 §9).
  'player.volume': 'Glasnost',
  'player.mute': 'Utišaj',
  'player.unmute': 'Vklopi zvok',
  // Brez besede „napaka", brez krivde: odločitev platforme, ne naša (P4 §5).
  // Stoji v razširjenem pogledu, ker je tam vidno, da je od glasnosti ostal
  // sam gumb za tišino (B-5, 2026-07-22).
  'player.volume.unsupported':
    'iPhone in iPad glasnost držita na strojnih tipkah. Tišina tu vseeno deluje, prav tako časovnik spanja.',

  // Razširjen pogled in način za avto (P2 §3.11, §7). Velelniki brez ogovora.
  'player.expand': 'Razširi',
  'player.collapse': 'Zapri',
  'player.expanded': 'Zdaj igra',
  'player.car': 'Način za avto',
  'player.car.exit': 'Zapusti način za avto',

  // {service} je znamka in ostane nespremenjena.
  'song.find': 'Poišči skladbo',
  'song.findOn': 'Poišči skladbo na {service}',

  'route.direct': 'Neposredno',
  'route.proxy': 'Prek našega strežnika',

  'meta.bitrate': '{n}{nbsp}kbps',

  'search.clear': 'Počisti iskanje',
  'stations.search.label': 'Iskanje po vaših postajah',
  'stations.search.placeholder': 'Iskanje',
  'stations.sort.label': 'Razvrsti',
  'stations.sort.custom': 'Po meri',
  'stations.sort.az': 'A–Ž',
  'stations.sort.za': 'Ž–A',
  'stations.sort.recent': 'Nedavno',
  'stations.favoritesOnly': 'Priljubljene',

  'stations.empty.title': 'Postaj še ni',
  'stations.empty.body': 'Dodajte svojo ali poiščite postaje na spletu.',
  'stations.empty.filtered.title': 'Nič se ne ujema',
  'stations.empty.filtered.body': 'Poskusite z drugim iskanjem ali izklopite filter priljubljenih.',

  'stations.add': 'Dodaj postajo',

  'station.play': 'Predvajaj {station}',
  'station.favourite': 'Dodaj {station} med priljubljene',
  'station.unfavourite': 'Odstrani {station} iz priljubljenih',
  'station.more': 'Več dejanj za {station}',
  // Velelnik brez ogovora (P4 §5). Skok na rob je ločeno dejanje od koraka.
  'station.moveTop': 'Premakni na vrh',
  'station.moveUp': 'Premakni gor',
  'station.moveDown': 'Premakni dol',
  'station.moveBottom': 'Premakni na dno',
  'station.edit': 'Uredi',
  'station.delete': 'Odstrani',
  // Vprašanje uporabniku je vikanje, dejanje je velelnik brez ogovora (P4 §5).
  'station.delete.title': 'Odstranim to postajo?',
  'station.delete.body':
    '{station} ne bo več na vašem seznamu. Če jo želite nazaj, jo najprej izvozite.',

  // Način urejanja (P2 §1.7). Gumb je velelnik in preklaplja med stanjema.
  'stations.reorder.start': 'Preuredi',
  'stations.reorder.done': 'Končaj',
  'stations.reorder.hint': 'Povlecite za ročico ali prek menija skočite na vrh ali dno.',
  'station.dragHandle': 'Povlecite {station} za premik',
  // Naznanilo živi regije ob premiku — ločeno od zapisa (D2 §52). „od N" ni
  // števna kategorija, ampak položaj, zato dvojina ni potrebna.
  'stations.reorder.moved': '{station} premaknjena na mesto {index} od {total}',

  // Web Share, kjer je; sicer odložišče — in to mora uporabnik izvedeti.
  'station.share': 'Deli',
  'station.share.aria': 'Deli {station}',
  'station.share.copied': 'Naslov je kopiran',

  'station.form.name': 'Ime',
  'station.form.url': 'Naslov toka',
  'station.form.tags': 'Žanri',
  'station.form.tags.hint': 'Ločite jih z vejico.',
  'station.form.country': 'Koda države',
  'station.form.country.hint': 'Dve črki, na primer SI ali US.',
  'station.form.error.name': 'Postaji dajte ime.',
  'station.form.error.url': 'Naslov se mora začeti s http:// ali https://.',
  'station.form.error.duplicate': 'Ta naslov je že na vašem seznamu.',

  // Dejanje je velelnik brez ogovora, navodilo uporabniku vikanje (P4 §5).
  'station.form.enrich': 'Poišči podatke',
  'station.form.enrich.busy': 'Iščem …',
  'station.form.enrich.done': 'Dopolnjeno s tem, kar je imel katalog.',
  'station.form.enrich.none': 'Za ta naslov ni ujemanja. Ostalo izpolnite sami.',
  'station.form.enrich.need': 'Najprej vnesite ime in naslov.',

  'dialog.close': 'Zapri',
  'dialog.cancel': 'Prekliči',
  'dialog.save': 'Shrani',

  'settings.data.heading': 'Vaši podatki',
  'settings.export.stations.title': 'Izvozi postaje',
  'settings.export.stations.desc': 'Samo seznam postaj — za deljenje z nekom drugim.',
  'settings.export.full.title': 'Izvozi vse',
  'settings.export.full.desc':
    'Postaje in nastavitve — za selitev na novo napravo ali kot varovalka.',
  'settings.export.action': 'Izvozi',
  // Prenos je na namizju viden, na mobilnem PWA pogosto ne — toast potrdi.
  'settings.export.toast.title': 'Izvoženo',
  'settings.export.toast.stations': 'Seznam postaj je shranjen.',
  'settings.export.toast.full': 'Shranjena je celotna kopija — postaje, zgodovina in nastavitve.',
  'settings.import.title': 'Uvozi',
  'settings.import.desc': 'Preden se karkoli zapiše, vidite, kaj se bo spremenilo.',
  'settings.import.action': 'Izberi datoteko',
  'settings.seed.title': 'Odstrani prednaložene postaje',
  'settings.seed.desc': 'Tiste, ki so prišle z aplikacijo. Vaše ostanejo.',
  'settings.seed.action': 'Odstrani',
  'settings.seed.confirm.title': 'Odstranim prednaložene postaje?',
  'settings.seed.confirm.body': 'Ne bodo se vrnile. Postaje, ki ste jih dodali sami, ostanejo.',
  'settings.seed.none': 'Že odstranjene',

  'settings.seed.restore.title': 'Dodaj prednaložene postaje',
  'settings.seed.restore.desc': 'Doda tiste, ki jih na seznamu ni. Vaše ostanejo nespremenjene.',
  'settings.seed.restore.action': 'Dodaj',
  'settings.seed.restore.none': 'Vse so tu',
  // ⚠️ Dvojina: vse štiri kategorije, `Intl.PluralRules('sl')` (P4 §9).
  'settings.seed.restore.count.one': 'Dodana bo {n} postaja',
  'settings.seed.restore.count.two': 'Dodani bosta {n} postaji',
  'settings.seed.restore.count.few': 'Dodane bodo {n} postaje',
  'settings.seed.restore.count.other': 'Dodanih bo {n} postaj',
  // Izid pristane na DRUGEM zaslonu (Postaje), zato toast potrdi tukaj.
  // ⚠️ Dvojina: vse štiri kategorije.
  'settings.seed.restore.toast.title': 'Dodano na seznam',
  'settings.seed.restore.toast.one': 'Dodana je {n} postaja',
  'settings.seed.restore.toast.two': 'Dodani sta {n} postaji',
  'settings.seed.restore.toast.few': 'Dodane so {n} postaje',
  'settings.seed.restore.toast.other': 'Dodanih je {n} postaj',

  // Počisti vse podatke (P2 §6.16) — najbolj rušilno dejanje v aplikaciji.
  'settings.reset.title': 'Počisti vse podatke',
  'settings.reset.desc':
    'Odstrani vse postaje, zgodovino in nastavitve. Najprej izvozite, če želite obdržati kopijo.',
  'settings.reset.action': 'Počisti vse',
  'settings.reset.confirm.title': 'Počistim vse podatke?',
  'settings.reset.confirm.body':
    'Vse vaše postaje, vsa zgodovina in nastavitve bodo izbrisane. Tega ni mogoče razveljaviti. Če ste prej izvozili kopijo, jo lahko pozneje uvozite.',
  'settings.reset.done.title': 'Podatki počiščeni',
  'settings.reset.done.body':
    'Aplikacija je spet prazna. Prednaložene postaje lahko dodate nazaj tukaj.',

  // Videz — tema in jezik (faza 5). Vsak je izbira enega iz dveh.
  'settings.appearance.heading': 'Videz',
  'settings.theme.title': 'Tema',
  'settings.theme.desc': 'Privzeto temna. Svetla prav tako deluje.',
  'settings.theme.dark': 'Temna',
  'settings.theme.light': 'Svetla',
  'settings.lang.title': 'Jezik',
  'settings.lang.desc': 'Angleščina ali slovenščina.',
  // Endonim: jezik se poimenuje v svojem jeziku, ne glede na vmesnik.
  'settings.lang.en': 'English',
  'settings.lang.sl': 'Slovenščina',

  /* ── Zvok: ekvalizer in vizualizator (faza 6, P2 §6.3–6.5) ────── */

  'settings.sound.heading': 'Zvok',

  'settings.eq.title': 'Ekvalizer',
  // Opozorilo iz T1 §4 / P2 §6.4. Velelnik brez ogovora, brez besede „napaka",
  // brez klicaja, brez krivde (P4 §4, §5).
  'settings.eq.desc':
    'Ekvalizer zahteva, da tok teče prek našega strežnika. Na počasnih mobilnih omrežjih se lahko zatika — takrat ga izklopite.',
  'settings.eq.unsupported': 'Safari na iPhonu in iPadu tega na živem toku ne zmore.',
  'settings.eq.unsupported.title': 'Tu ni na voljo',
  'settings.eq.unsupported.body':
    'Safari na iPhonu in iPadu živega toka ne zna obdelati. Predvajanje deluje naprej.',

  'settings.eq.preset': 'Prednastavitev',
  'settings.eq.preset.flat': 'Ravno',
  'settings.eq.preset.rock': 'Rock',
  'settings.eq.preset.pop': 'Pop',
  'settings.eq.preset.jazz': 'Jazz',
  'settings.eq.preset.classical': 'Klasika',
  'settings.eq.preset.bass': 'Poudarjen bas',
  'settings.eq.preset.treble': 'Poudarjene višine',
  'settings.eq.preset.vocal': 'Govor',
  'settings.eq.preset.custom': 'Po meri',
  'settings.eq.reset': 'Poravnaj',

  // ⚠️ Dvojina: 1 decibel · 2 decibela · 3 decibele · 5 decibelov (P4 §9).
  // `Intl.PluralRules` izbira po absolutni vrednosti, zato velja tudi za −3.
  'settings.eq.db.one': '{value} decibel',
  'settings.eq.db.two': '{value} decibela',
  'settings.eq.db.few': '{value} decibele',
  'settings.eq.db.other': '{value} decibelov',
  'settings.eq.hz': '{n}{nbsp}Hz',
  'settings.eq.khz': '{n}{nbsp}kHz',

  'settings.visualizer.title': 'Spekter',
  // Okras in nič drugega (CLAUDE.md, pravilo 1) — in ima isto ceno kot EQ.
  'settings.visualizer.desc': 'Samo okras. Zahteva isto pot prek našega strežnika.',

  /* ── Tipkovnica (faza 7, P2 §8.8) ──────────────────────────────── */

  // Bližnjica, ki je nikjer ne piše, ne obstaja. Opis je pomembnejši od
  // seznama: brez njega bi presledek sredi iskanja ustavil zvok in videti bi
  // bilo kot napaka. Vikanje, ker je to pojasnilo uporabniku (P4 §4).
  'settings.keys.heading': 'Tipkovnica',
  'settings.keys.desc': 'Bližnjice delujejo povsod, razen kadar tipkate v polje.',
  'settings.keys.space': 'Preslednica',
  'settings.keys.playPause': 'Predvajaj ali ustavi',
  'settings.keys.previous': 'Prejšnja postaja',
  'settings.keys.next': 'Naslednja postaja',
  'settings.keys.volumeUp': 'Glasneje',
  'settings.keys.volumeDown': 'Tišje',
  'settings.keys.mute': 'Utišaj ali vklopi zvok',

  /* ── Zavihki Nastavitev (B-4) ───────────────────────────────────── */

  // »Vizitka« in ne »o aplikaciji«: zavihek pokriva tipkovnico, o aplikaciji
  // IN zasebnost, zato ne sme nositi imena enega od svojih razdelkov.
  'settings.tabs.label': 'Razdelki nastavitev',
  'settings.info.heading': 'Vizitka',

  'settings.about.heading': 'O aplikaciji',
  'settings.about.version': 'Različica {version}{nbsp}·{nbsp}{build}',
  // ⚠️ P1 §4 to zavezo zapiše s tikanjem (»tvoje postaje«), ker je interni
  // dokument. V vmesniku tikanja ni: pojasnilo uporabniku je vikanje (P4 §2).
  'settings.about.desc': 'Radio, ki se odpre in igra. Brez reklam, brez računa, brez čakanja.',
  'settings.about.promise':
    'Vaše postaje ostanejo vaše — na napravi in v vrstnem redu, ki ste ga postavili. Ko se tok prekine, to vidite: ponovni poskus je viden in ne tih.',
  'settings.about.licences':
    'Imenik postaj vzdržujeta skupnostna projekta radio-browser in WebRadioDB.',

  /* ── Politika zasebnosti (faza 7, P2 §6.15) ─────────────────────── */

  // Vikanje, ker je to pojasnilo uporabniku (P4 §2). Slovenščina je tu izvirnik
  // in ne prevod: stavki so krajši od angleških tam, kjer slovenščina to
  // dopušča, in ne sledijo angleški zgradbi.
  'settings.privacy.heading': 'Zasebnost',
  'settings.privacy.intro':
    'Nesywave nima računov, oglasov ne analitike. Postaje, nastavitve, zgodovina in čas poslušanja ostanejo na tej napravi.',
  'settings.privacy.updated': 'Zadnja sprememba {date}',

  'settings.privacy.device.title': 'Kaj ostane na napravi',
  'settings.privacy.device.body':
    'Postaje, nastavitve, zgodovina poslušanja in statistika so v tem brskalniku, v shrambah localStorage in IndexedDB. Kopije na našem strežniku ni, ker ni računa, na katerega bi jo bilo mogoče vezati. Izvoz zapiše datoteko, ki jo hranite sami; uvoz prebere samo datoteko, ki jo izberete. Če izbrišete podatke brskalnika za to stran, izgine vse.',

  'settings.privacy.server.title': 'Kaj gre skozi naš strežnik',
  'settings.privacy.server.intro':
    'Zvok teče privzeto naravnost z naprave do postaje. Naš strežnik na wave.nesylab.com se vmeša samo v teh primerih.',
  'settings.privacy.server.stream':
    'Prenos toka — kadar postaja nima naslova HTTPS ali kadar vklopite ekvalizer ali vizualizator.',
  'settings.privacy.server.probe':
    'Sonda za HTTPS — kadar se naslov postaje začne s http://. Strežnik pred povezavo pri Cloudflarovem razreševalniku DNS preveri, ali naslov kaže v zasebno omrežje, in ga v tem primeru zavrne.',
  'settings.privacy.server.catalog':
    'Imenik postaj — med iskanjem. Poizvedbe gredo v radio-browser, ob izpadu v WebRadioDB, odgovori pa se hranijo 24 ur.',
  'settings.privacy.server.art':
    'Naslovi skladb in ovitki — med predvajanjem. Najprej metapodatki same postaje, nato MusicBrainz, Cover Art Archive in Deezer. Apple Music povpraša brskalnik sam.',
  'settings.privacy.server.limit':
    'Vsak zahtevek nosi naslov IP. Strežnik ga prišteje k omejitvi 60 zahtevkov na minuto in nato pozabi. Strežnik gostuje pri Cloudflaru, ki hrani kratke tehnične dnevnike; kaj poslušate, ni zapisano nikjer.',

  'settings.privacy.station.title': 'Kaj vidi postaja',
  'settings.privacy.station.body':
    'Kadar zvok teče direktno, se naprava pogovarja s postajo sama, zato njen upravljavec vidi zahtevek tako kot vsaka spletna stran: naslov IP, čas in oznako brskalnika. Tako deluje internetni radio in ni naša izbira. Če bi vse pošiljali prek našega strežnika, bi to skrili postaji in pokazali nam.',

  'settings.privacy.external.title': 'Odhod iz aplikacije',
  'settings.privacy.external.body':
    '»Poišči skladbo« odpre Apple Music, Spotify ali YouTube v novem zavihku, kjer veljajo njihova pravila in naša ne več. Ko postajo iz imenika predposlušate, brskalnik javi en klik nazaj v radio-browser — tako imenik izve, katere postaje so v rabi. Klik nosi oznako postaje, radio-browser pa zahtevek vidi tako kot vsaka spletna stran.',

  'settings.privacy.none.title': 'Česa Nesywave ne počne',
  'settings.privacy.none.body':
    'Ni računa, ni prijave, ni oglasov, ni analitike, ni sledenja. Piškotkov Nesywave ne postavlja. Ničesar ne prodamo in ne posredujemo naprej, ker ničesar ne zberemo.',

  'backup.import.title': 'Uvoz postaj',
  // ⚠️ Vse štiri kategorije: 1 postaja · 2 postaji · 3 postaje · 5 postaj (P4 §9).
  'backup.preview.total.one': '{n} postaja v datoteki',
  'backup.preview.total.two': '{n} postaji v datoteki',
  'backup.preview.total.few': '{n} postaje v datoteki',
  'backup.preview.total.other': '{n} postaj v datoteki',
  'backup.preview.added.one': '{n} je nova',
  'backup.preview.added.two': '{n} sta novi',
  'backup.preview.added.few': '{n} so nove',
  'backup.preview.added.other': '{n} jih je novih',
  'backup.preview.duplicate.one': '{n} je že na seznamu',
  'backup.preview.duplicate.two': '{n} sta že na seznamu',
  'backup.preview.duplicate.few': '{n} so že na seznamu',
  'backup.preview.duplicate.other': '{n} jih je že na seznamu',
  'backup.preview.settings': 'Nastavitve iz datoteke bodo prepisale vaše.',
  'backup.mode.merge': 'Združi',
  'backup.mode.merge.desc': 'Ohrani, kar imate, in doda, kar je novega.',
  'backup.mode.replace': 'Zamenjaj',
  'backup.mode.replace.desc': 'Vaš seznam odpade, prevzame ga datoteka.',
  'backup.import.action': 'Uvozi',
  'backup.error.unreadable': 'Te datoteke ni bilo mogoče prebrati.',
  'backup.error.not-a-backup': 'To ni izvoz iz Nesywave.',
  'backup.error.too-new': 'Datoteka je iz novejše različice Nesywave.',
  'backup.error.migration-failed': 'Datoteke ni bilo mogoče pretvoriti v trenutno obliko.',

  'genre.pop': 'Pop',
  'genre.rock': 'Rock',
  'genre.jazz': 'Jazz',
  'genre.classical': 'Klasika',
  'genre.electronic': 'Elektronika',
  'genre.ambient': 'Ambient',
  'genre.dance': 'Plesna',
  'genre.folk': 'Narodnozabavna',
  'genre.indie': 'Indie',
  'genre.talk': 'Govorna',
  'genre.news': 'Novice',
  'genre.culture': 'Kultura',
  'genre.religious': 'Verska',

  /* ── Iskanje po spletni bazi (faza 4, P2 §2) ───────────────────── */

  'search.field.label': 'Iskanje po spletnem imeniku',
  'search.field.placeholder': 'Ime postaje',
  'search.filter.country': 'Država',
  'search.filter.country.any': 'Vse države',
  'search.filter.genre': 'Žanr',
  'search.filter.genre.any': 'Vsi žanri',
  // Combobox (DS `.cmb`, od v4.20.10). Gumba nosita samo ikono, zato je ime
  // v `aria-label` — velelnik brez ogovora, kot vsak drug gumb (P4 §4).
  'search.filter.clear': 'Počisti filter',
  'search.filter.open': 'Odpri seznam',
  'search.filter.noMatch': 'Ni ujemanj',
  'search.filter.count': '{n}',

  // ⚠️ Dvojina: vse štiri kategorije, ne `count === 1 ? a : b` (P4 §9).
  'search.results.count.one': '{n} najdena postaja',
  'search.results.count.two': '{n} najdeni postaji',
  'search.results.count.few': '{n} najdene postaje',
  'search.results.count.other': '{n} najdenih postaj',

  // Prvi obisk ni prazen rezultat: nič se še ni iskalo in pot naprej je druga.
  'search.start.title': 'Poiščite postajo',
  'search.start.body':
    'Vpišite ime ali izberite državo oziroma žanr in pokazale se bodo najbolj poslušane.',
  'search.empty.title': 'Ni zadetkov',
  'search.empty.body': 'Poskusite s krajšim imenom ali odstranite enega od filtrov.',

  // Brez besede „napaka", brez kod, brez klicajev, brez krivde (P4 §5).
  'search.failed.title': 'Imenik ni odgovoril',
  'search.failed.body': 'Seznam postaj prihaja iz storitve, ki je občasno zasedena.',
  'search.failed.action': 'Poskusi znova',
  'search.offline.title': 'Ni povezave',
  'search.offline.body': 'Iskanje potrebuje povezavo. Vaše postaje igrajo naprej.',

  'search.loading': 'Iskanje…',
  'search.more': 'Pokaži več',
  'search.preview': 'Predposlušaj {station}',
  'search.add': 'Dodaj',
  'search.add.aria': 'Dodaj {station} med moje postaje',
  'search.added': 'Na seznamu',
  'search.added.aria': '{station} je že na vašem seznamu',
  'search.source.fallback': 'Iz rezervnega imenika',

  'station.insecure.badge': 'HTTP',
  'station.insecure.title': 'Postaja ne oddaja varno',
  'station.insecure.hint': 'Predvajanje poteka prek našega strežnika.',

  /* ── Zgodovina (P2 §4) ─────────────────────────────────────────── */

  'history.search.label': 'Iskanje po zgodovini',
  'history.search.placeholder': 'Iskanje',
  'history.clearAll': 'Počisti vse',
  // Vprašanje uporabniku; dejanje je velelnik brez ogovora (P4 §5).
  'history.clearAll.confirm.title': 'Počistim vso zgodovino?',
  'history.clearAll.confirm.body':
    'Predvajane skladbe in čas poslušanja bodo izbrisani. Tega ni mogoče razveljaviti.',
  'history.clearStation': 'Počisti zgodovino za {station}',
  'history.clearStation.action': 'Počisti',
  'history.clearStation.confirm.title': 'Počistim zgodovino te postaje?',
  'history.clearStation.confirm.body':
    'Njene predvajane skladbe in čas poslušanja bodo izbrisani. Druge postaje ostanejo.',
  'history.copy': 'Kopiraj {track}',
  'history.copied': 'Kopirano',
  // »24. jul. 14:32« — vejica za piko okrajšave bi bila odveč (P4 §7).
  'history.at.yesterday': 'Včeraj, {time}',
  'history.at.dated': '{date} {time}',
  'history.unknownStation': 'Odstranjena postaja',
  'history.empty.title': 'Zgodovine še ni',
  'history.empty.body': 'Skladbe, ki jih slišite, se bodo zbirale tukaj, združene po postaji.',
  'history.empty.filtered.title': 'Nič se ne ujema',
  'history.empty.filtered.body': 'Poskusite z drugim iskanjem.',

  /* ── Časovnik spanja (P2 §5) ───────────────────────────────────── */

  'timer.presets.heading': 'Prednastavitve',
  'timer.preset': '{n}{nbsp}min',
  'timer.custom.heading': 'Poljuben čas',
  'timer.hours': 'Ure',
  'timer.minutes': 'Minute',
  'timer.start': 'Zaženi',
  'timer.cancel': 'Prekliči časovnik',
  'timer.minus': '−5{nbsp}min',
  'timer.plus': '+5{nbsp}min',
  'timer.minus.aria': 'Pet minut manj',
  'timer.plus.aria': 'Pet minut več',
  'timer.remaining': 'Še {time}',
  'timer.active': 'Ko časovnik poteče, se predvajanje ustavi.',
  'timer.fading': 'Pojemanje…',
};
